<template>
  <Dialog
    title="课程上架"
    :visible.sync="dialogVisible"
    width="640px"
    :close-on-click-modal="false"
    :before-close="cancel"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
      <el-form-item label="上下架时间" prop="shelfStartTime">
        <el-date-picker
          v-model="shelfDateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="上架时间"
          end-placeholder="下架时间"
          style="width: 100%"
          @change="changeShelfDateRange"
        />
      </el-form-item>
      <el-form-item label="开结课时间" prop="startTime">
        <el-date-picker
          v-model="courseDateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开课时间"
          end-placeholder="结课时间"
          style="width: 100%"
          @change="changeCourseDateRange"
        />
      </el-form-item>
      <el-form-item label="负责教师" prop="managerCode">
        <el-select
          v-model="form.managerCode"
          placeholder="请选择负责教师"
          clearable
          filterable
          style="width: 100%"
        >
          <el-option
            v-for="item in managerOptions"
            :key="item.teacherCode"
            :label="item.teacherName"
            :value="item.teacherCode"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="主讲教师" prop="speakerCode">
        <el-select
          v-model="form.speakerCode"
          placeholder="请选择主讲教师"
          clearable
          filterable
          style="width: 100%"
        >
          <el-option
            v-for="item in speakerOptions"
            :key="item.teacherCode"
            :label="item.teacherName"
            :value="item.teacherCode"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="课程类型" prop="courseType">
        <el-select
          v-model="form.courseType"
          placeholder="请选择课程类型"
          style="width: 100%"
          @change="handleCourseTypeChange"
        >
          <el-option
            v-for="(item, index) in courseTypeList"
            :key="item.dictCode || `${item.dictValue}-${index}`"
            :label="item.dictLabel"
            :value="item.dictValue"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="推荐分类" prop="recommendCategoryList">
        <el-select
          v-model="form.recommendCategoryList"
          multiple
          placeholder="请选择推荐分类"
          style="width: 100%"
        >
          <el-option
            v-for="item in recommendCategoryOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="职业路径" prop="learningPathList">
        <el-select
          v-model="form.learningPathList"
          multiple
          filterable
          placeholder="请选择职业路径"
          style="width: 100%"
        >
          <el-option
            v-for="item in learningPathOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="学习路径" prop="learningSystem">
        <el-select
          v-model="form.learningSystem"
          placeholder="请选择学习路径"
          style="width: 100%"
        >
          <el-option
            v-for="item in learningSystemOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button plain round size="small" @click="cancel">取 消</el-button>
      <el-button
        plain
        round
        size="small"
        type="primary"
        :loading="submitLoading"
        @click="submitForm"
        >确认上架</el-button
      >
    </div>
  </Dialog>
</template>

<script>
import { getCourseDetail, updateCourseStatus } from "@/api/course";
import { getTeacherAll } from "@/api/teacher";
import {
  ensureDictCache,
  getDictOptions,
  getIntDictOptions,
  DICT_TYPE
} from "@/utils/dict";

const COURSE_BODY_KEYS = [
  "categoryIdOne",
  "categoryIdTwo",
  "courseCode",
  "courseId",
  "courseImage",
  "courseIntroduce",
  "courseName",
  "courseTime",
  "courseType",
  "courseware",
  "endTime",
  "isNeedSignUp",
  "learningPath",
  "learningSystem",
  "managerCode",
  "recommendCategory",
  "shelfEndTime",
  "shelfStartTime",
  "signUpEndTime",
  "signUpStartTime",
  "speakerCode",
  "startTime",
  "status",
  "videoPercent"
];

function pickCourseBody(source = {}) {
  const body = {};
  COURSE_BODY_KEYS.forEach(key => {
    if (source[key] !== undefined) {
      body[key] = source[key];
    }
  });
  return body;
}

function formatDateForApi(value) {
  if (!value) return "";
  return `${String(value).slice(0, 10)} 00:00:00`;
}

function sliceDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function splitCsv(value) {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) return value.map(String);
  return String(value)
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);
}

function toIntOrUndefined(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

function defaultForm() {
  return {
    courseCode: "",
    courseId: undefined,
    shelfStartTime: "",
    shelfEndTime: "",
    startTime: "",
    endTime: "",
    managerCode: "",
    speakerCode: "",
    courseType: "",
    recommendCategoryList: [],
    learningPathList: [],
    learningSystem: undefined,
    isNeedSignUp: 0
  };
}

export default {
  name: "CourseShelfForm",
  data() {
    const validateShelfRange = (rule, value, callback) => {
      if (!this.form.shelfStartTime || !this.form.shelfEndTime) {
        callback(new Error("请选择上下架时间"));
        return;
      }
      if (this.form.endTime && this.form.endTime < this.form.shelfEndTime) {
        callback(new Error("结课时间必须晚于或等于下架时间"));
        return;
      }
      callback();
    };
    const validateCourseRange = (rule, value, callback) => {
      if (!this.form.startTime || !this.form.endTime) {
        callback(new Error("请选择开结课时间"));
        return;
      }
      if (
        this.form.shelfEndTime &&
        this.form.endTime < this.form.shelfEndTime
      ) {
        callback(new Error("结课时间必须晚于或等于下架时间"));
        return;
      }
      callback();
    };
    return {
      dialogVisible: false,
      submitLoading: false,
      form: defaultForm(),
      courseDetail: {},
      shelfDateRange: [],
      courseDateRange: [],
      managerOptions: [],
      speakerOptions: [],
      courseTypeList: [],
      learningSystemOptions: [],
      learningPathOptions: [],
      recommendCategoryOptions: [],
      rules: {
        shelfStartTime: [
          { required: true, validator: validateShelfRange, trigger: "change" }
        ],
        startTime: [
          { required: true, validator: validateCourseRange, trigger: "change" }
        ],
        managerCode: [
          { required: true, message: "请选择负责教师", trigger: "change" }
        ],
        speakerCode: [
          { required: true, message: "请选择主讲教师", trigger: "change" }
        ],
        courseType: [
          { required: true, message: "请选择课程类型", trigger: "change" }
        ],
        recommendCategoryList: [
          {
            type: "array",
            required: true,
            min: 1,
            message: "请选择推荐分类",
            trigger: "change"
          }
        ],
        learningPathList: [
          {
            type: "array",
            required: true,
            min: 1,
            message: "请选择职业路径",
            trigger: "change"
          }
        ],
        learningSystem: [
          { required: true, message: "请选择学习路径", trigger: "change" }
        ]
      }
    };
  },
  computed: {
    isEncryptedCourse() {
      const type = this.form.courseType;
      if (type === "" || type === null || type === undefined) return false;
      // 字典 course_type：1=预约课（需报名）
      if (Number(type) === 1) return true;
      const item = this.courseTypeList.find(
        opt => opt.dictValue === type || String(opt.dictValue) === String(type)
      );
      const label = String((item && item.dictLabel) || "");
      return label.includes("加密") || label.includes("预约");
    }
  },
  methods: {
    async loadCourseTypeOptions() {
      await ensureDictCache();
      const list = getIntDictOptions(DICT_TYPE.COURSE_TYPE);
      const seen = {};
      this.courseTypeList = list.filter(item => {
        const key = String(item.dictValue);
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      });
      this.learningSystemOptions = getIntDictOptions(
        DICT_TYPE.LEARNING_SYSTEM
      ).map(item => ({
        value: item.dictValue,
        label: item.dictLabel
      }));
      this.learningPathOptions = getDictOptions(DICT_TYPE.LEARNING_PATH).map(
        item => ({
          value: String(item.dictValue),
          label: item.dictLabel
        })
      );
      this.recommendCategoryOptions = getDictOptions(
        DICT_TYPE.RECOMMEND_TYPE
      ).map(item => ({
        value: String(item.dictValue),
        label: item.dictLabel
      }));
    },
    normalizeList(result) {
      const data = result && result.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.rows)) return data.rows;
      return [];
    },
    async loadManagers() {
      try {
        const [managerRes, speakerRes] = await Promise.all([
          getTeacherAll("1"),
          getTeacherAll("2")
        ]);
        this.managerOptions = this.normalizeList(managerRes).filter(
          item => item.teacherCode
        );
        this.speakerOptions = this.normalizeList(speakerRes).filter(
          item => item.teacherCode
        );
      } catch (e) {}
    },
    changeShelfDateRange(value) {
      if (value && value.length === 2) {
        this.form.shelfStartTime = value[0];
        this.form.shelfEndTime = value[1];
      } else {
        this.form.shelfStartTime = "";
        this.form.shelfEndTime = "";
      }
      this.$nextTick(() => {
        if (!this.$refs.formRef) return;
        this.$refs.formRef.validateField("shelfStartTime");
        this.$refs.formRef.validateField("startTime");
      });
    },
    changeCourseDateRange(value) {
      if (value && value.length === 2) {
        this.form.startTime = value[0];
        this.form.endTime = value[1];
      } else {
        this.form.startTime = "";
        this.form.endTime = "";
      }
      this.$nextTick(() => {
        if (!this.$refs.formRef) return;
        this.$refs.formRef.validateField("startTime");
        this.$refs.formRef.validateField("shelfStartTime");
      });
    },
    handleCourseTypeChange() {
      if (this.isEncryptedCourse) {
        this.form.isNeedSignUp = 0;
      }
    },
    async open(row = {}) {
      this.dialogVisible = true;
      this.loadManagers();
      await this.loadCourseTypeOptions();
      this.form = defaultForm();
      this.courseDetail = {};
      this.shelfDateRange = [];
      this.courseDateRange = [];
      this.form.courseCode = row.courseCode || "";
      try {
        const result = await getCourseDetail(row.courseCode);
        const data = (result && result.data) || row;
        this.courseDetail = data || {};
        this.form = {
          ...defaultForm(),
          courseCode: data.courseCode || row.courseCode || "",
          courseId: data.courseId,
          shelfStartTime: sliceDate(data.shelfStartTime),
          shelfEndTime: sliceDate(data.shelfEndTime),
          startTime: sliceDate(data.startTime),
          endTime: sliceDate(data.endTime),
          managerCode: data.managerCode || "",
          speakerCode: data.speakerCode || "",
          courseType:
            data.courseType === null || data.courseType === undefined
              ? ""
              : toIntOrUndefined(data.courseType),
          recommendCategoryList: splitCsv(data.recommendCategory),
          learningPathList: splitCsv(data.learningPath),
          learningSystem:
            data.learningSystem === null || data.learningSystem === undefined
              ? undefined
              : Number(data.learningSystem),
          isNeedSignUp: Number(data.isNeedSignUp) || 0
        };
        if (this.form.shelfStartTime && this.form.shelfEndTime) {
          this.shelfDateRange = [
            this.form.shelfStartTime,
            this.form.shelfEndTime
          ];
        }
        if (this.form.startTime && this.form.endTime) {
          this.courseDateRange = [this.form.startTime, this.form.endTime];
        }
        this.handleCourseTypeChange();
      } catch (e) {
        this.form.courseCode = row.courseCode || "";
        this.courseDetail = { ...row };
      }
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate();
      });
    },
    cancel(done) {
      this.dialogVisible = false;
      this.form = defaultForm();
      this.courseDetail = {};
      this.shelfDateRange = [];
      this.courseDateRange = [];
      if (typeof done === "function") done();
    },
    async submitForm() {
      if (this.submitLoading) return;
      try {
        await this.$refs.formRef.validate();
      } catch (e) {
        return;
      }
      this.submitLoading = true;
      try {
        // 文档要求 body 为完整 Course，基于详情合并上架字段
        const payload = pickCourseBody({
          ...this.courseDetail,
          courseCode: this.form.courseCode,
          courseId: this.form.courseId,
          status: 0,
          shelfStartTime: formatDateForApi(this.form.shelfStartTime),
          shelfEndTime: formatDateForApi(this.form.shelfEndTime),
          startTime: formatDateForApi(this.form.startTime),
          endTime: formatDateForApi(this.form.endTime),
          managerCode: this.form.managerCode,
          speakerCode: this.form.speakerCode,
          courseType: toIntOrUndefined(this.form.courseType),
          recommendCategory: (this.form.recommendCategoryList || []).join(","),
          learningPath: (this.form.learningPathList || []).join(","),
          learningSystem: toIntOrUndefined(this.form.learningSystem),
          isNeedSignUp: this.isEncryptedCourse
            ? 0
            : toIntOrUndefined(this.form.isNeedSignUp)
        });
        // 详情 VO 里日期可能带时分秒，报名时间一并规范
        if (payload.signUpStartTime) {
          payload.signUpStartTime = formatDateForApi(payload.signUpStartTime);
        }
        if (payload.signUpEndTime) {
          payload.signUpEndTime = formatDateForApi(payload.signUpEndTime);
        }
        await updateCourseStatus(payload);
        this.$message.success("上架成功");
        this.cancel();
        this.$emit("success");
      } catch (e) {
      } finally {
        this.submitLoading = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/views/Course/CourseShelfForm.vue